import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { LucideAngularModule } from 'lucide-angular';
import { TabsModule } from 'primeng/tabs';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { ObjectivesCompletionService } from '../../shared/services/objectives-completion.service';
import { ReductionTargetComponent } from './components/reduction-target/reduction-target.component';
import { ClimateActionsComponent } from './components/climate-actions/climate-actions.component';
import { ClimateRisksComponent } from './components/climate-risks/climate-risks.component';

type ObjectivesTab = 'target' | 'actions' | 'risks';

const OBJECTIVES_TABS: ObjectivesTab[] = ['target', 'actions', 'risks'];

function isObjectivesTab(value: string | null): value is ObjectivesTab {
  return value !== null && OBJECTIVES_TABS.includes(value as ObjectivesTab);
}

@Component({
  selector: 'app-objectives-page',
  standalone: true,
  imports: [
    TranslocoModule,
    LucideAngularModule,
    TabsModule,
    PageHeaderComponent,
    ReductionTargetComponent,
    ClimateActionsComponent,
    ClimateRisksComponent,
  ],
  templateUrl: './objectives-page.component.html',
})
export class ObjectivesPageComponent {
  private route = inject(ActivatedRoute);
  private objectivesCompletionService = inject(ObjectivesCompletionService);

  activeTab = signal<ObjectivesTab>('target');

  // A warning dot marks the tabs the company has not filled in yet — the same
  // signal that lights up the "Reduction objectives" entry in the menu.
  tabs = computed<
    { id: ObjectivesTab; labelKey: string; iconName: string; warning: boolean }[]
  >(() => [
    {
      id: 'target',
      labelKey: 'objectives.tabTarget',
      iconName: 'target',
      warning: this.objectivesCompletionService.needsReductionTarget(),
    },
    {
      id: 'actions',
      labelKey: 'objectives.tabActions',
      iconName: 'leaf',
      warning: this.objectivesCompletionService.needsCommitments(),
    },
    {
      id: 'risks',
      labelKey: 'objectives.tabRisks',
      iconName: 'triangle-alert',
      warning: false,
    },
  ]);

  constructor() {
    // Deep-linkable tab: the onboarding journey sends users straight to the tab a step is
    // about (?tab=actions for the commitments, ?tab=risks for the climate risks). Subscribed
    // rather than read from the snapshot so a second journey link still switches tabs while
    // the page is already open — Angular reuses the component on a query-param change.
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
      const requested = params.get('tab');
      if (isObjectivesTab(requested)) {
        this.activeTab.set(requested);
      }
    });
  }

  setTab(tab: ObjectivesTab): void {
    this.activeTab.set(tab);
  }
}
